import React from "react";
import { Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import MemberAvatar from "@/components/common/MemberAvatar";
import ProgramBadge from "@/components/sponsors/ProgramBadge";
import AvailabilityPill from "@/components/sponsors/AvailabilityPill";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Sponsor Profiles tab: review and remove published sponsor profiles.
 *
 * Extracted verbatim from src/pages/Admin.jsx during the code-quality pass:
 * markup, copy, data-testids and behaviour are unchanged - only the file
 * boundary moved.
 */
export default function AdminSponsorProfiles({ profiles, busy, setBusy, loadAll }) {
  return (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-profiles">
          {profiles.length === 0 ? (
            <p className="text-sm text-[#4B5A60]">No sponsor profiles yet.</p>
          ) : (
            profiles.map((p) => (
              <div key={p.user_id} className="sm-card p-5 bg-white">
                <div className="flex items-center gap-3">
                  <MemberAvatar user={p.user} size={44} />
                  <div className="min-w-0">
                    <p className="font-semibold text-[#1F2A2E] truncate">{p.user?.display_name}</p>
                    <p className="text-xs text-[#7A868B] truncate">{p.email}</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <ProgramBadge program={p.recovery_program} />
                  <AvailabilityPill status={p.availability_status} />
                  {p.is_paused && (
                    <span className="px-2 py-1 rounded-full bg-[#FBF0DC] text-xs font-semibold text-[#8A5A12]">
                      Paused
                    </span>
                  )}
                </div>
                <p className="mt-3 text-sm text-[#4B5A60] line-clamp-3">{p.about_me}</p>
                <div className="mt-4 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`/sponsors/${p.user_id}`, "_blank")}
                    className="bg-white border-[#E3DED4]"
                  >
                    Review
                  </Button>
                  <Button
                    size="sm"
                    onClick={async () => {
                      setBusy(p.user_id);
                      try {
                        await api.delete(`/admin/sponsor-profiles/${p.user_id}`);
                        toast.success("Sponsor profile removed.");
                        await loadAll();
                      } catch (e) {
                        toast.error(apiError(e));
                      } finally {
                        setBusy("");
                      }
                    }}
                    disabled={busy === p.user_id}
                    data-testid={`remove-profile-${p.user_id}`}
                    className="bg-[#B42318] hover:bg-[#94180F] text-white"
                  >
                    {busy === p.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Remove profile"}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
  );
}
